/**
 * Career Report Service
 * Manages career report data in Firestore
 */

import { db, auth } from '../firebase';
import { 
  collection, 
  doc, 
  setDoc, 
  getDoc, 
  getDocs,
  updateDoc, 
  deleteDoc,
  query, 
  where, 
  orderBy,
  serverTimestamp 
} from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';

const CAREER_REPORTS_COLLECTION = 'careerReports';

// Plan status constants
export const PLAN_STATUS = {
  DRAFT: 'draft',       // Created but no progress made
  PARTIAL: 'partial',   // Some progress, not currently active
  ACTIVE: 'active',     // Currently active plan (only 1 allowed)
  COMPLETED: 'completed' // Training plan 100% complete (milestone achieved)
};

// Limits
const FREE_USER_MONTHLY_LIMIT = 2;
const MAX_SAVED_REPORTS = 5;
const DRAFT_EXPIRY_DAYS = 30;

/**
 * Save career report data to Firestore
 * @param {Object} reportData - Career report data
 * @param {string} userId - User ID
 * @returns {Promise<string>} Document ID
 */
export async function saveCareerReport(reportData, userId) {
  try {
    const careerReportsRef = collection(db, CAREER_REPORTS_COLLECTION);
    
    // If no document ID provided, create new one
    const docId = reportData.id || doc(careerReportsRef).id;
    const docRef = doc(db, CAREER_REPORTS_COLLECTION, docId);

    const reportToSave = {
      userId,
      status: reportData.status || 'pending',
      createdAt: reportData.createdAt || serverTimestamp(),
      updatedAt: serverTimestamp(),
      
      // Plan status system
      planStatus: reportData.planStatus || PLAN_STATUS.DRAFT,
      isActive: reportData.isActive !== undefined ? reportData.isActive : true, // New reports are active by default
      lastProgressAt: reportData.lastProgressAt || null,
      completedAt: reportData.completedAt || null,
      
      // Input data
      resumeText: reportData.resumeText || null,
      resumeData: reportData.resumeData || null,
      preprocessedData: reportData.preprocessedData || null,
      careerPath: reportData.careerPath || null,
      targetPosition: reportData.targetPosition || null,
      targetRole: reportData.targetRole || null,
      pivotField: reportData.pivotField || null,
      currentRole: reportData.currentRole || null,
      selectedSuggestion: reportData.selectedSuggestion || null,
      learningStyle: reportData.learningStyle || null,
      timeline: reportData.timeline || null,
      additionalContext: reportData.additionalContext || {},
      
      // Analysis results (initially null)
      jobRequirements: reportData.jobRequirements || null,
      gapAnalysis: reportData.gapAnalysis || null,
      trainingPlan: reportData.trainingPlan || null,
      readinessScore: reportData.readinessScore || null,
      applicationReady: reportData.applicationReady || false,
      
      // PDF
      pdfUrl: reportData.pdfUrl || null,
      pdfGenerated: reportData.pdfGenerated || false,
      
      // Metadata
      regenerated: reportData.regenerated || false,
      paymentCompleted: reportData.paymentCompleted || false,
      error: reportData.error || null
    };

    await setDoc(docRef, reportToSave, { merge: true });
    
    console.log('✅ Career report saved:', docId);
    return docId;
  } catch (error) {
    console.error('❌ Error saving career report:', error);
    throw error;
  }
}

/**
 * Check if user can create a new report (monthly limit)
 * @param {string} userId - User ID
 * @param {boolean} isAdmin - Is user admin
 * @returns {Promise<{canCreate: boolean, currentCount: number, limit: number, message: string}>}
 */
export async function checkReportLimit(userId, isAdmin = false) {
  try {
    // Admins have unlimited reports
    if (isAdmin) {
      return { canCreate: true, currentCount: 0, limit: Infinity, message: 'Admin: Unlimited reports' };
    }

    // Get reports created this month
    const now = new Date();
    const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
    
    const reportsRef = collection(db, CAREER_REPORTS_COLLECTION);
    const q = query(
      reportsRef,
      where('userId', '==', userId)
    );
    
    const querySnapshot = await getDocs(q);
    
    // Count reports created this month
    let monthlyCount = 0;
    querySnapshot.forEach((doc) => {
      const data = doc.data();
      const createdAt = data.createdAt?.toDate?.() || new Date(data.createdAt);
      if (createdAt >= startOfMonth) {
        monthlyCount++;
      }
    });

    const canCreate = monthlyCount < FREE_USER_MONTHLY_LIMIT;
    
    return {
      canCreate,
      currentCount: monthlyCount,
      limit: FREE_USER_MONTHLY_LIMIT,
      message: canCreate 
        ? `${monthlyCount}/${FREE_USER_MONTHLY_LIMIT} reports used this month`
        : `Monthly limit reached (${FREE_USER_MONTHLY_LIMIT}). Upgrade to Premium for unlimited reports.`
    };
  } catch (error) {
    console.error('❌ Error checking report limit:', error);
    // Default to allowing if check fails
    return { canCreate: true, currentCount: 0, limit: FREE_USER_MONTHLY_LIMIT, message: 'Could not verify limit' };
  }
}

/**
 * Set a report as the active plan (deactivates all others and updates profile)
 * @param {string} reportId - Report ID to set as active
 * @param {string} userId - User ID
 * @returns {Promise<void>}
 */
export async function setActiveReport(reportId, userId) {
  try {
    const reportsRef = collection(db, CAREER_REPORTS_COLLECTION);
    const q = query(reportsRef, where('userId', '==', userId));
    const querySnapshot = await getDocs(q);

    // Find the report being activated and deactivate others
    const batch = [];
    let activeReportData = null;
    
    querySnapshot.forEach((docSnapshot) => {
      const docRef = doc(db, CAREER_REPORTS_COLLECTION, docSnapshot.id);
      const data = docSnapshot.data();
      
      if (docSnapshot.id === reportId) {
        // Set as active and store the data
        activeReportData = { id: docSnapshot.id, ...data };
        batch.push(updateDoc(docRef, {
          isActive: true,
          planStatus: PLAN_STATUS.ACTIVE,
          updatedAt: serverTimestamp()
        }));
      } else if (data.isActive) {
        // Mark as partial if it had progress, otherwise draft
        const hasProgress = data.lastProgressAt || 
                           (data.trainingPlan?.summary?.completed > 0);
        batch.push(updateDoc(docRef, {
          isActive: false,
          planStatus: hasProgress ? PLAN_STATUS.PARTIAL : PLAN_STATUS.DRAFT,
          updatedAt: serverTimestamp()
        }));
      }
    });

    await Promise.all(batch);
    
    // Update the user's profile with the new active plan's data
    if (activeReportData) {
      const profileId = `${userId}_career`;
      const profileRef = doc(db, 'profiles', profileId);
      
      // Update profile with the active report's target role, training plan, and gap analysis
      const profileUpdates = {
        targetRole: activeReportData.targetRole || activeReportData.pivotField || '',
        careerReportId: reportId,
        updatedAt: serverTimestamp()
      };
      
      // Only update these fields if the report has them (completed reports)
      if (activeReportData.gapAnalysis) {
        profileUpdates.gapAnalysis = activeReportData.gapAnalysis;
        profileUpdates.qualificationPercentage = activeReportData.gapAnalysis.qualificationPercentage || 0;
      }
      
      // Transform career report training plan structure to profile structure
      // Career reports store: trainingPlan.detailedPlan.trainingItems
      // Profiles expect: trainingPlan.items
      if (activeReportData.trainingPlan) {
        const reportTrainingPlan = activeReportData.trainingPlan;
        
        // Extract items from various possible locations in the report structure
        let trainingItems = [];
        if (reportTrainingPlan.items && Array.isArray(reportTrainingPlan.items)) {
          // Already in profile format
          trainingItems = reportTrainingPlan.items;
        } else if (reportTrainingPlan.detailedPlan?.trainingItems) {
          // New flat structure from detailedResourceRecommender
          trainingItems = reportTrainingPlan.detailedPlan.trainingItems;
        } else if (reportTrainingPlan.detailedPlan?.phases) {
          // Legacy phase-based structure
          const phases = reportTrainingPlan.detailedPlan.phases;
          ['shortTerm', 'midTerm', 'longTerm'].forEach(phaseKey => {
            const phase = phases[phaseKey];
            if (phase?.activities) {
              trainingItems.push(...phase.activities.map((a, idx) => ({
                id: `${phaseKey}_${idx}`,
                ...a,
                phase: phaseKey,
                status: a.status || 'not_started'
              })));
            }
          });
        }
        
        // Ensure each item has an id
        trainingItems = trainingItems.map((item, idx) => ({
          ...item,
          id: item.id || `item_${idx}`,
          status: item.status || 'not_started'
        }));
        
        // Calculate summary
        const completed = trainingItems.filter(i => i.status === 'completed').length;
        const total = trainingItems.length;
        
        profileUpdates.trainingPlan = {
          items: trainingItems,
          summary: {
            completed,
            total,
            percentage: total > 0 ? Math.round((completed / total) * 100) : 0
          },
          timeToReadiness: reportTrainingPlan.detailedPlan?.timeToReadiness || reportTrainingPlan.timeToReadiness || '',
          totalBudgetEstimate: reportTrainingPlan.detailedPlan?.totalBudgetEstimate || reportTrainingPlan.totalBudgetEstimate || '',
          totalHours: reportTrainingPlan.detailedPlan?.totalHours || reportTrainingPlan.totalHours || ''
        };
        
        console.log(`✅ Transformed ${trainingItems.length} training items for profile`);
      }
      
      if (activeReportData.readinessScore !== undefined) {
        profileUpdates.readinessScore = activeReportData.readinessScore;
      }
      
      await updateDoc(profileRef, profileUpdates);
      console.log('✅ Profile updated with active report data');
    }
    
    console.log('✅ Set active report:', reportId);
  } catch (error) {
    console.error('❌ Error setting active report:', error);
    throw error;
  }
}

/**
 * Record progress on a report (updates lastProgressAt)
 * @param {string} reportId - Report ID
 * @returns {Promise<void>}
 */
export async function recordProgress(reportId) {
  try {
    const docRef = doc(db, CAREER_REPORTS_COLLECTION, reportId);
    await updateDoc(docRef, {
      lastProgressAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    });
    console.log('✅ Progress recorded for report:', reportId);
  } catch (error) {
    console.error('❌ Error recording progress:', error);
    throw error;
  }
}

/**
 * Mark a report as completed (milestone achieved)
 * @param {string} reportId - Report ID
 * @returns {Promise<void>}
 */
export async function markReportCompleted(reportId) {
  try {
    const docRef = doc(db, CAREER_REPORTS_COLLECTION, reportId);
    await updateDoc(docRef, {
      planStatus: PLAN_STATUS.COMPLETED,
      completedAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    });
    console.log('✅ Report marked as completed:', reportId);
  } catch (error) {
    console.error('❌ Error marking report completed:', error);
    throw error;
  }
}

/**
 * Delete multiple reports (bulk delete)
 * @param {Array<string>} reportIds - Array of report IDs to delete
 * @returns {Promise<{deleted: number, failed: number}>}
 */
export async function bulkDeleteReports(reportIds) {
  let deleted = 0;
  let failed = 0;

  for (const reportId of reportIds) {
    try {
      await deleteCareerReport(reportId);
      deleted++;
    } catch (error) {
      console.error(`Failed to delete report ${reportId}:`, error);
      failed++;
    }
  }

  return { deleted, failed };
}

/**
 * Get career reports for a user
 * @param {string} userId - User ID
 * @returns {Promise<Array>} Array of career reports
 */
export async function getCareerReports(userId) {
  try {
    const careerReportsRef = collection(db, CAREER_REPORTS_COLLECTION);
    const q = query(
      careerReportsRef,
      where('userId', '==', userId),
      orderBy('createdAt', 'desc')
    );

    const querySnapshot = await getDocs(q);
    const reports = [];

    querySnapshot.forEach((doc) => {
      reports.push({
        id: doc.id,
        ...doc.data()
      });
    });

    console.log(`📊 Loaded ${reports.length} career reports for user`);
    return reports;
  } catch (error) {
    console.error('❌ Error getting career reports:', error);
    throw error;
  }
}

/**
 * Get a single career report by ID
 * @param {string} reportId - Report ID
 * @returns {Promise<Object>} Career report data
 */
export async function getCareerReport(reportId) {
  try {
    const docRef = doc(db, CAREER_REPORTS_COLLECTION, reportId);
    const docSnap = await getDoc(docRef);

    if (docSnap.exists()) {
      return {
        id: docSnap.id,
        ...docSnap.data()
      };
    } else {
      throw new Error('Career report not found');
    }
  } catch (error) {
    console.error('❌ Error getting career report:', error);
    throw error;
  }
}

/**
 * Update career report
 * @param {string} reportId - Report ID
 * @param {Object} updates - Updates to apply
 * @returns {Promise<void>}
 */
export async function updateCareerReport(reportId, updates) {
  try {
    const docRef = doc(db, CAREER_REPORTS_COLLECTION, reportId);
    await updateDoc(docRef, {
      ...updates,
      updatedAt: serverTimestamp()
    });
    
    console.log('✅ Career report updated:', reportId);
  } catch (error) {
    console.error('❌ Error updating career report:', error);
    throw error;
  }
}

/**
 * Delete career report
 * @param {string} reportId - Report ID
 * @returns {Promise<void>}
 */
export async function deleteCareerReport(reportId) {
  try {
    const docRef = doc(db, CAREER_REPORTS_COLLECTION, reportId);
    await deleteDoc(docRef);
    
    console.log('✅ Career report deleted:', reportId);
  } catch (error) {
    console.error('❌ Error deleting career report:', error);
    throw error;
  }
}

/**
 * Trigger career path analysis (calls Cloud Function)
 * @param {string} reportId - Report ID
 * @returns {Promise<Object>} Analysis result
 */
export async function analyzeCareerPath(reportId) {
  try {
    const functions = getFunctions();
    // Extended timeout for Grok web search - can take 3-5 minutes
    const analyzeFunction = httpsCallable(functions, 'analyzeCareerPath', { timeout: 300000 }); // 5 minutes
    
    console.log('🚀 Calling analyzeCareerPath function...');
    const result = await analyzeFunction({ reportId });
    
    console.log('✅ Career analysis complete');
    return result.data;
  } catch (error) {
    console.error('❌ Error analyzing career path:', error);
    throw error;
  }
}

/**
 * Mark report as regenerated (for one-time free regeneration)
 * @param {string} reportId - Report ID
 * @returns {Promise<void>}
 */
export async function markReportAsRegenerated(reportId) {
  try {
    await updateCareerReport(reportId, { regenerated: true });
  } catch (error) {
    console.error('❌ Error marking report as regenerated:', error);
    throw error;
  }
}

